import _ from "lodash";
import React from "react";
import classes from "./DropDown.scss";
import I18nHOC from "UIComponents/I18nHOC";
import ACLStore from "lib/aclStore";
import {
  CURRICULUM_TYPE_DP,
  CURRICULUM_TYPE_MYP,
  CURRICULUM_TYPE_PYP,
  CURRICULUM_TYPE_UBD,
  AI_TUTOR,
  CREATE,
} from "Constants/stringConstants";
import { Dropdown, DropdownMenu, Avatar } from "@toddle-edu/ds-web";
import {
  AddOutlined,
  FaOutlined,
  LeLibraryOutlined,
  SaOutlined,
  UnitPlanOutlined,
  ImportOutlined,
  ToddleAcademyOutlined,
  JournalOutlined,
} from "@toddle-edu/ds-icons";
import { ENABLE_ASSESSMENTS_IN_PYP } from "Constants/permissionConstants";
import {
  canImportLEsFromToddleLibrary,
  canImportWorksheetFromToddleLibrary,
} from "ToddleLibrary/modules/ToddleLibraryUtils";
import { getIsAssessmentTypeConfigurationEnabled } from "Utils/ACLStoreUtils";
import { getTaskTypeAvatarName } from "Utils/EntityFlowUtils";

const getMenuItems = ({
  t,
  label,
  curriculumType,
  assessmentTypeGroup,
  globalGradeUid,
  isAssessmentTypeConfigurationEnabled,
}) => {
  const importSubGroup = {
    label: t("common:import_from"),
    key: "IMPORT",
    isItemGroup: true,
    options: [
      {
        label: t("unitPlan:other_unit_of_this_academic_year"),
        key: "ADD_FROM_OTHER_UNIT",
        isDisable: false,
        icon: <UnitPlanOutlined variant={"subtle"} />,
        isVisible: ACLStore.can("TeacherPortal:ImportLeFromOtherUnits"),
      },
      {
        label: ACLStore.can(ENABLE_ASSESSMENTS_IN_PYP)
          ? t("community:le_&_assessment_library")
          : t("common:le_library"),
        isDisable: false,
        key: "SEARCH_LE_LIBRARY",
        icon: <LeLibraryOutlined variant={"subtle"} />,
        isVisible:
          ACLStore.can("TeacherPortal:LELibrary") &&
          _.isEqual(curriculumType, CURRICULUM_TYPE_PYP),
      },
      {
        label: t("community:le_&_assessment_library"),
        isDisable: false,
        key: "SEARCH_LE_LIBRARY",
        icon: <LeLibraryOutlined variant={"subtle"} />,
        isVisible:
          ACLStore.can("TeacherPortal:LELibrary") &&
          !_.isEqual(curriculumType, CURRICULUM_TYPE_PYP),
      },
      {
        label: t("common:toddle_sample_unit_with_curriculum", {
          curriculum: _.isEqual(curriculumType, "IB_MYP") ? "MYP" : "UbD",
        }),
        key: "ADD_FROM_TODDLE_LIBRARY",
        isDisable: false,
        icon: <ToddleAcademyOutlined variant={"subtle"} />,
        isVisible:
          ACLStore.can("TeacherPortal:ImportFromToddleLibrary") &&
          _.includes(
            [CURRICULUM_TYPE_MYP, CURRICULUM_TYPE_UBD],
            curriculumType
          ),
      },
      {
        label: t("common:toddle_library"),
        key: "IMPORT_LE_FROM_TODDLE_LIBRARY",
        icon: <JournalOutlined variant={"subtle"} />,
        isDisable: false,
        isVisible: canImportLEsFromToddleLibrary({
          curriculumType,
          globalGradeUid,
        }),
      },
    ],
  };

  const shouldUseImportSubGroup =
    importSubGroup.options.filter(
      option => !option.isDisable && !!option.isVisible
    ).length > 1;

  if (
    _.isEqual(assessmentTypeGroup, "WORKSHEET") &&
    !isAssessmentTypeConfigurationEnabled
  ) {
    return [
      {
        label: t("common:create_new"),
        icon: <AddOutlined variant={"subtle"} />,
        isDisable: false,
        key: "ADD_WORKSHEET",
        value: "ADD_WORKSHEET",
        isVisible: true,
      },
      {
        isDivider: true,
        key: "DIVIDER",
      },
      {
        label: t("common:import_from"),
        key: "IMPORT",
        isItemGroup: true,
        options: [
          {
            label: t("unitPlan:other_unit_of_this_academic_year"),
            key: "ADD_FROM_OTHER_UNIT",
            isDisable: false,
            icon: <UnitPlanOutlined variant={"subtle"} />,
            isVisible: ACLStore.can("TeacherPortal:ImportLeFromOtherUnits"),
          },
          {
            label: t("common:library"),
            isDisable: false,
            key: "IMPORT_WORKSHEET",
            icon: <LeLibraryOutlined variant={"subtle"} />,
            isVisible: ACLStore.can("FeatureFlag:LeFeedWorksheet"),
          },
          {
            label: t("common:toddle_library"),
            key: "IMPORT_WORKSHEET_FROM_TODDLE_LIBRARY",
            icon: <JournalOutlined variant={"subtle"} />,
            isDisable: false,
            isVisible: canImportWorksheetFromToddleLibrary({ curriculumType }),
          },
        ],
      },
    ];
  }

  if (
    _.isEqual(assessmentTypeGroup, AI_TUTOR) &&
    !isAssessmentTypeConfigurationEnabled
  ) {
    return [
      {
        label: t("common:create_new"),
        icon: <AddOutlined variant={"subtle"} />,
        isDisable: false,
        key: AI_TUTOR,
        value: AI_TUTOR,
        isVisible: true,
      },
    ];
  }

  return [
    {
      label: t("common:create_new"),
      icon: <AddOutlined variant={"subtle"} />,
      isDisable: false,
      key: "CREATE",
      isVisible: true,
    },
    {
      isDivider: true,
      key: "DIVIDER",
    },
    shouldUseImportSubGroup
      ? importSubGroup
      : {
          label: t("classRoom:import_unit"),
          icon: <ImportOutlined variant={"subtle"} />,
          isVisible: ACLStore.can("TeacherPortal:ImportLeFromOtherUnits"),
        },
  ];
};

const DropDown = ({
  renderButtonComponent,
  onItemClick,
  t,
  label,
  curriculumType,
  groupType,
  assessmentTypeGroup,
  globalGradeUid,
  isUnitFlowRevamp,
  testId,
  dynamicCreateOptions,
}) => {
  const isAssessmentTypeConfigurationEnabled = getIsAssessmentTypeConfigurationEnabled();

  let menuItems = getMenuItems({
    t,
    label,
    curriculumType,
    assessmentTypeGroup,
    globalGradeUid,
    isAssessmentTypeConfigurationEnabled,
  });

  // Skip static assessment type logic when dynamic configuration is enabled
  // as the dynamic options will be handled later
  if (
    !isAssessmentTypeConfigurationEnabled &&
    groupType == "ASSESSMENT" &&
    (_.includes([CURRICULUM_TYPE_DP, CURRICULUM_TYPE_MYP], curriculumType) ||
      (_.includes([CURRICULUM_TYPE_PYP], curriculumType) &&
        ACLStore.can(ENABLE_ASSESSMENTS_IN_PYP)))
  ) {
    //Remove Create Assessment Option and Add Summative Assessment and Formative Assessment Option

    menuItems = _.filter(
      menuItems,
      item => !_.includes(["CREATE", "DIVIDER"], item.key)
    );
    menuItems.unshift({
      label: t("common:create"),
      key: "CREATE",
      isItemGroup: true,
      options: [
        {
          label: t("common:formative_assessment"),
          isDisable: false,
          key: "CREATE_FMT_ASSESSMENT",
          icon: <FaOutlined variant={"subtle"} />,
          isVisible: true,
        },
        {
          label: t("common:summative_assessment"),
          isDisable: false,
          key: "CREATE_SMT_ASSESSMENT",
          icon: <SaOutlined variant={"subtle"} />,
          isVisible: true,
        },
      ],
    });
  }

  // Skip static UBD assessment type logic when dynamic configuration is enabled
  if (
    !isAssessmentTypeConfigurationEnabled &&
    groupType == "ASSESSMENT" &&
    curriculumType == "UBD"
  ) {
    // Remove Create Assessment Option and Add Summative Assessment and Formative Assessment Option
    menuItems = _.filter(menuItems, item => item.key != "CREATE");

    if (assessmentTypeGroup == "SECOND_STAGE_ASSESSMENT") {
      menuItems.unshift({
        label: t("common:create_new_with_label", {
          label: t("common:stage_2_assessment"),
        }),
        isDisable: false,
        icon: <AddOutlined variant={"subtle"} />,
        key: "CREATE_PT_ASSESSMENT",
        isVisible: true,
      });
    } else if (assessmentTypeGroup == "THIRD_STAGE_ASSESSMENT") {
      menuItems.unshift({
        label: t("common:create_new_with_label", {
          label: t("common:stage_3_assessment"),
        }),
        isDisable: false,
        icon: <AddOutlined variant={"subtle"} />,
        key: "CREATE_PRI_ASSESSMENT",
        isVisible: true,
      });
    } else {
      menuItems.unshift({
        label: t("common:create_new_with_label", {
          label: t("common:assessment_label"),
        }),
        isDisable: false,
        icon: <AddOutlined variant={"subtle"} />,
        key: "CREATE_PT_ASSESSMENT",
        isVisible: true,
      });
    }
  }

  // When dynamic assessment type configuration is enabled and dynamicCreateOptions are provided,
  // use them instead of static options for the create section
  if (
    isAssessmentTypeConfigurationEnabled &&
    !_.isEmpty(dynamicCreateOptions)
  ) {
    // Remove existing CREATE-related items and DIVIDER (not import options)
    menuItems = _.filter(
      menuItems,
      item =>
        !_.includes(
          [
            CREATE,
            "CREATE_FMT_ASSESSMENT",
            "CREATE_SMT_ASSESSMENT",
            "CREATE_PT_ASSESSMENT",
            "CREATE_PRI_ASSESSMENT",
            "DIVIDER",
          ],
          _.get(item, "key")
        ) &&
        !(_.get(item, "isItemGroup") && _.isEqual(_.get(item, "key"), CREATE))
    );

    // Format dynamic options for the dropdown
    const formattedDynamicOptions = _.map(dynamicCreateOptions, option => {
      const taskTypeValue = _.get(option, "taskType", "");
      const taskTypeLabel = _.get(option, "label", "");
      const taskTypeId = _.get(option, "taskTypeId", "");
      const color = _.get(option, "color", "");
      const templateId = _.get(option, "templateId", "");

      const avatarName = getTaskTypeAvatarName({
        type: taskTypeValue,
        label: taskTypeLabel,
        isAssessmentTypeConfigurationEnabled,
      });

      return {
        label: taskTypeLabel,
        key: `DYNAMIC_CREATE_${taskTypeId}`,
        value: `DYNAMIC_CREATE_${taskTypeId}`,
        taskTypeId,
        templateId,
        taskType: taskTypeValue,
        isDisable: false,
        isVisible: true,
        icon: (
          <Avatar
            shape="square"
            size="small"
            name={avatarName}
            color={color}
            dsVersion="2.0"
          />
        ),
      };
    });

    // If only one option, show "Create new" label
    if (_.isEqual(_.size(formattedDynamicOptions), 1)) {
      const singleOption = _.first(formattedDynamicOptions);
      // Insert at the beginning, with a divider before import options
      menuItems = _.concat(
        [
          {
            label: t("common:create_new"),
            key: _.get(singleOption, "key"),
            value: _.get(singleOption, "value"),
            taskTypeId: _.get(singleOption, "taskTypeId"),
            templateId: _.get(singleOption, "templateId"),
            taskType: _.get(singleOption, "taskType"),
            icon: <AddOutlined variant={"subtle"} />,
            isDisable: false,
            isVisible: true,
          },
          {
            isDivider: true,
            key: "DIVIDER",
          },
        ],
        menuItems
      );
    } else if (_.gt(_.size(formattedDynamicOptions), 1)) {
      // Multiple options - show as item group
      menuItems = _.concat(
        [
          {
            label: t("common:create"),
            key: "CREATE_GROUP",
            isItemGroup: true,
            options: formattedDynamicOptions,
          },
        ],
        menuItems
      );
    }
  }

  menuItems = _.reduce(
    menuItems,
    (result, menuItem) => {
      if (!_.get(menuItem, "isItemGroup")) {
        // current menuItem is not an item group
        if (
          !_.get(menuItem, "isDisable", false) &&
          !!_.get(menuItem, "isVisible", true)
        ) {
          result.push(menuItem);
        }
      } else {
        const currentOptions = _.get(menuItem, "options", []);
        const updatedOptions = _.filter(
          currentOptions,
          option =>
            !!_.get(option, "isVisible", true) &&
            !_.get(option, "isDisable", false)
        );
        if (_.get(updatedOptions, "length", 0) > 0) {
          // add the current itemGroup only if it has atleast one option in it
          result.push({ ...menuItem, options: updatedOptions });
        }
      }
      return result;
    },
    []
  );
  const isWorksheet = _.isEqual(assessmentTypeGroup, "WORKSHEET"); // TODO:WORKSHEET - Remove when worksheet library is ready
  const isAITutor = _.isEqual(assessmentTypeGroup, AI_TUTOR);

  const ContainerClasses = isUnitFlowRevamp
    ? classes.containerRevamped
    : classes.container;

  return (
    <div className={ContainerClasses}>
      {_.size(menuItems) > 1 || isWorksheet || isAITutor ? (
        <Dropdown
          overlay={
            <DropdownMenu
              options={menuItems}
              dsVersion={isUnitFlowRevamp ? "2.0" : "1.0"}
              onClick={({ key }) => onItemClick(key)}
              testId={testId}
            />
          }
        >
          {renderButtonComponent()}
        </Dropdown>
      ) : (
        <div
          role="button"
          tabIndex={0}
          className={classes.buttonComponent}
          onClick={() =>
            onItemClick(_.get(menuItems, ["0", "value"], "CREATE"))
          }
          onKeyDown={e => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              onItemClick(_.get(menuItems, ["0", "value"], "CREATE"));
            }
          }}
        >
          {renderButtonComponent({ singleButton: true })}
        </div>
      )}
    </div>
  );
};

export default I18nHOC({ resource: ["community", "classRoom"] })(DropDown);
